import { useState } from 'react';
import { Copy, Check, ShieldAlert, X } from 'lucide-react';

/**
 * Shows a generated password once.
 *
 * The server does not keep it and there is no mailer to send it, so this
 * dialog is the only moment it exists in readable form. That is worth saying
 * plainly on screen — an owner who closes this thinking they can look it up
 * later has locked out the person they just hired.
 */
export default function CredentialHandoff({ email, password, onClose }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4"
         role="dialog" aria-modal="true" aria-labelledby="handoff-title">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-50">
            <ShieldAlert className="h-5 w-5 text-amber-700" aria-hidden="true" />
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <h2 id="handoff-title" className="mt-3 text-lg font-extrabold tracking-tight text-slate-900">
          Copy this password now
        </h2>
        <p className="mt-1.5 text-sm text-slate-600">
          It is shown once and is not stored anywhere. If it is lost, issue a new one from the
          staff list — it cannot be looked up.
        </p>

        <dl className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <dt className="text-[12px] font-bold uppercase tracking-wide text-slate-500">Sign in as</dt>
          <dd className="mt-0.5 break-all font-semibold text-slate-900">{email}</dd>
          <dt className="mt-3 text-[12px] font-bold uppercase tracking-wide text-slate-500">Temporary password</dt>
          <dd className="mt-1 flex items-center gap-2">
            <code className="flex-1 rounded-lg bg-white px-3 py-2 font-mono text-base tracking-wide text-slate-900 ring-1 ring-slate-200">
              {password}
            </code>
            <button type="button" onClick={copy}
                    aria-label={copied ? 'Copied' : 'Copy password'}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-slate-300 text-brand-600 hover:bg-white">
              {copied
                ? <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                : <Copy className="h-4 w-4" aria-hidden="true" />}
            </button>
          </dd>
        </dl>

        <p className="mt-3 text-xs text-slate-500">
          The hyphens are only there to make it readable aloud — it works typed either way.
          They will be asked to choose their own password the first time they sign in.
        </p>

        <button type="button" onClick={onClose}
                className="mt-5 w-full rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-700">
          I have saved it
        </button>
      </div>
    </div>
  );
}
